import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import {
  isOnboardingComplete,
  isActivationPending,
  getCompletedStepsFromStorage,
} from "../../Utils/onboardingNavigation";
import { confirmActivationState } from "../../Utils/activationSync";
import { ExploreIcon, JourneyIcon, CaptureIcon, MenuIcon, ArisMark } from "./icons";
import "./style.css";

/** Window event the header listens for: the Menu tab opens its menu. */
export const OPEN_MENU_EVENT = "aris:toggle-menu";

/**
 * Bottom navigation (Figma 9862:23510): Explore, Journey, the raised Aris
 * button in the middle, Capture, and Menu.
 *
 * `Aris` is /dashboard, which the bar wraps rather than replacing. Explore,
 * Journey and Capture are their own routes and are withheld until the member
 * has finished activation — see `requiresActivation` below. Menu is not a
 * route: it opens the header's menu, which the header renders outside the
 * router, so it is asked through a window event.
 *
 * Rendered once by RootLayout and self-hiding, so no page has to know about it.
 */
export const HOME_TABS = [
  // `theme` is the backdrop each section renders on, which decides the bar's own
  // palette: the blue Aris home takes the translucent glass treatment, while
  // sections on the light gradient background take the frosted white one.
  //
  // `requiresActivation` withholds a tab while the member is mid-activation.
  // Aris is never withheld — activation happens *in* a Talk session, so removing
  // it would strand the member with nowhere to go.
  {
    id: "explore",
    label: "Explore",
    path: "/explore",
    Icon: ExploreIcon,
    theme: "light",
    requiresActivation: true,
  },
  {
    id: "journey",
    label: "Journey",
    path: "/journey",
    Icon: JourneyIcon,
    theme: "light",
    requiresActivation: true,
  },
  { id: "aris", label: "Aris", path: "/dashboard", theme: "dark", center: true },
  {
    id: "capture",
    label: "Capture",
    path: "/capture",
    Icon: CaptureIcon,
    theme: "light",
    requiresActivation: true,
  },
  { id: "menu", label: "Menu", Icon: MenuIcon, action: OPEN_MENU_EVENT },
];

function HomeTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCoachingActive } = useHeader();

  // Activation can finish mid-session, so this is state rather than a value read
  // once at mount: the tabs must appear as soon as the profile records it.
  const [steps, setSteps] = useState(getCompletedStepsFromStorage);

  useEffect(() => {
    const refresh = () => setSteps(getCompletedStepsFromStorage());

    // `storage` only fires for *other* tabs, so same-tab writes need an explicit
    // signal. Pages that refresh the profile dispatch `profile-updated`.
    window.addEventListener("storage", refresh);
    window.addEventListener("profile-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("profile-updated", refresh);
    };
  }, []);

  // Re-read on navigation too: finishing activation ends with a route change, and
  // that covers callers that update the profile without dispatching.
  useEffect(() => {
    setSteps(getCompletedStepsFromStorage());
  }, [location.pathname, location.search]);

  // The cached profile answers instantly, so the bar never flickers. But
  // completion is recorded asynchronously on the backend (an EventBridge consumer
  // reacting to aris-ai), and nothing refetches the profile on navigation — so a
  // cache saying "pending" may be stale. Confirm against the authoritative gate,
  // and only for members the cache says are pending.
  const cachedPending = isActivationPending(steps);
  const [gatePending, setGatePending] = useState(null);

  useEffect(() => {
    if (!cachedPending) {
      setGatePending(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      // `confirmActivationState` also refreshes the cached profile when the gate
      // says complete. This runs on every navigation, so it is where completion
      // is usually noticed first — flipping only local state here left
      // `localStorage.profile` saying `activation_pending` forever: tabs
      // appeared, every other reader of the cache disagreed, and each
      // navigation paid for another gate call that changed nothing.
      //
      // null means the request failed: keep trusting the cache rather than
      // guessing, so a flaky network cannot reveal or hide tabs incorrectly.
      const pending = await confirmActivationState();
      if (!cancelled && pending !== null) setGatePending(pending);
    })();
    return () => {
      cancelled = true;
    };
  }, [cachedPending, location.pathname]);

  const activationPending = gatePending ?? cachedPending;
  const tabs = HOME_TABS.filter(
    (tab) => !(tab.requiresActivation && activationPending)
  );

  const path = location.pathname || "";
  const activeTab = tabs.find((tab) => tab.path && tab.path === path);
  const routeTabs = tabs.filter((tab) => tab.path);

  // The Aris tab carries its session on the same route via `?mode=`, so a mode
  // means a conversation is on screen — not the tab root. The bar stands down so
  // it cannot cover the chat input or interrupt a voice session.
  const isSessionSurface =
    path === "/dashboard" && new URLSearchParams(location.search).has("mode");

  // Mid-onboarding, /dashboard renders OnboardingQRPage instead of the home —
  // showing tabs there would offer sections the member cannot use yet.
  const onboardingIncomplete = steps.length > 0 && !isOnboardingComplete(steps);

  // A live voice session takes over the screen: HeaderComponent already hides
  // itself on isCoachingActive, and the bar follows suit so the session stays
  // immersive and cannot be navigated away from by accident.
  //
  // Mid-activation only the Aris section is left, and a bar with one section is
  // not a choice — the first-conversation screen (Figma 10112:5108) has no bar.
  const visible =
    !!activeTab &&
    routeTabs.length > 1 &&
    !isSessionSurface &&
    !isCoachingActive &&
    !onboardingIncomplete;

  // Body hook so pages can reserve room for the fixed bar. Set here rather than
  // per-page: the bar decides when it exists, so it owns the flag.
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add("has-home-tabbar");
    return () => document.body.classList.remove("has-home-tabbar");
  }, [visible]);

  if (!visible) return null;

  const goTo = (tab) => {
    if (tab.action) {
      window.dispatchEvent(new Event(tab.action));
      return;
    }
    if (tab.path === path) return;
    // Defensive: the bar is hidden during an active session, but a connected
    // idle session can still be playing audio.
    if (window.stopCoachingAudio) window.stopCoachingAudio();
    // useNavigate is correct here because the bar lives inside RouterProvider.
    // The header menu cannot use it — it renders outside the router and must
    // fall back to pushState + a synthetic popstate.
    navigate(tab.path);
  };

  return (
    <nav
      className={`aris-tabbar${
        activeTab.theme === "light" ? " aris-tabbar--on-light" : ""
      }`}
      aria-label="Home sections"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab.id;
        if (tab.center) {
          return (
            <button
              key={tab.id}
              type="button"
              className={`aris-tabbar__aris${isActive ? " is-active" : ""}`}
              aria-label={tab.label}
              aria-current={isActive ? "page" : undefined}
              onClick={() => goTo(tab)}
            >
              <span className="aris-tabbar__aris-glow" aria-hidden="true" />
              <span className="aris-tabbar__aris-disc">
                <ArisMark />
              </span>
            </button>
          );
        }
        return (
          <button
            key={tab.id}
            type="button"
            className={`aris-tabbar__tab${isActive ? " is-active" : ""}`}
            aria-current={isActive ? "page" : undefined}
            onClick={() => goTo(tab)}
          >
            <span className="aris-tabbar__icon">
              <tab.Icon />
            </span>
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}

export default HomeTabBar;
